/**
 * 모형 LOD(S5 — 커널/자산 쪽이 model.l1.glb · model.l2.glb 와 매니페스트 lodPaths 를 더한다).
 * 계약은 **선택 필드만**: LOD 주소가 없으면 늘 원본(L0 = glbUrl)을 쓰고, LOD 를 못 받으면 한 단계씩 올라가 L0 로 되돌아간다.
 *  - 전시관 정지(멀리 · 가만히 있는) 작품 = L2 → L1 → L0
 *  - 친구 기계(FS-03-03) · 전시관에서 작동 중인 작품 = L1 → L0
 *  - 내 기계 · 고스트 · 미리보기 = L0
 * 읽기는 너그럽게: {lod:{l1,l2}} · {lodUrls:{l1,l2}|[l1,l2]} · {lodGlbUrls:…} · 매니페스트 {lodPaths:{l1,l2}}(자산 라이브러리 기준 경로 —
 * glbUrl 의 같은 라이브러리 경로 앞에 붙인다. 커널 S5: model.l1.lod1.glb · model.l2.lod1.glb, 노드·재질은 L0 와 같다).
 */
export interface LodUrls {
  l1?: string | null;
  l2?: string | null;
}

export type LodLevel = 0 | 1 | 2;

export interface LodRef {
  glbUrl: string | null;
  lod?: LodUrls | null;
}

/**
 * 상대 경로 풀기 — 절대·루트 경로는 그대로. 매니페스트 lodPaths 는 자산 라이브러리 기준(campus-models/<slug>/<ver>/model.l1.lod1.glb)이라
 * glbUrl 에서 같은 라이브러리 경로가 시작하는 곳 앞을 기준으로 붙이고, 파일 이름만이면 glbUrl 폴더 기준으로.
 */
function resolveAgainst(path: string, glbUrl: string | null): string {
  if (/^[a-z][a-z0-9+.-]*:/i.test(path) || path.startsWith("/") || !glbUrl) return path;
  const dir = path.includes("/") ? path.slice(0, path.lastIndexOf("/") + 1) : "";
  const first = path.split("/")[0] ?? "";
  for (const needle of [dir, first ? `${first}/` : ""]) {
    if (!needle) continue;
    const at = glbUrl.indexOf(needle);
    if (at >= 0) return `${glbUrl.slice(0, at)}${path}`;
  }
  const cut = glbUrl.lastIndexOf("/");
  return cut >= 0 ? `${glbUrl.slice(0, cut + 1)}${path}` : path;
}

const pair = (v: unknown): LodUrls | null => {
  if (Array.isArray(v)) {
    const [l1, l2] = v;
    return {
      l1: typeof l1 === "string" && l1 ? l1 : null,
      l2: typeof l2 === "string" && l2 ? l2 : null,
    };
  }
  if (v && typeof v === "object") {
    const o = v as Record<string, unknown>;
    const l1 = o.l1 ?? o.L1 ?? o["1"];
    const l2 = o.l2 ?? o.L2 ?? o["2"];
    return {
      l1: typeof l1 === "string" && l1 ? l1 : null,
      l2: typeof l2 === "string" && l2 ? l2 : null,
    };
  }
  return null;
};

/** 계약 객체(전시 작품 · run-bundle asset · 매니페스트 모형)에서 LOD 주소를 읽는다. 없으면 null */
export function readLod(v: unknown, glbUrl: string | null = null): LodUrls | null {
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  const base = glbUrl ?? (typeof o.glbUrl === "string" ? o.glbUrl : null);
  const raw = pair(o.lod) ?? pair(o.lodUrls) ?? pair(o.lodGlbUrls) ?? pair(o.lodPaths);
  if (!raw || (!raw.l1 && !raw.l2)) return null;
  return {
    l1: raw.l1 ? resolveAgainst(raw.l1, base) : null,
    l2: raw.l2 ? resolveAgainst(raw.l2, base) : null,
  };
}

/** 이 단계에서 시도할 주소(앞이 먼저) — 마지막은 늘 L0. 없는 단계는 건너뛴다 */
export function lodCandidates(ref: LodRef, level: LodLevel): string[] {
  const out: string[] = [];
  if (level >= 2 && ref.lod?.l2) out.push(ref.lod.l2);
  if (level >= 1 && ref.lod?.l1) out.push(ref.lod.l1);
  if (ref.glbUrl) out.push(ref.glbUrl);
  return [...new Set(out)];
}

/** 단계별 주소를 차례로 받아 본다 — 모두 실패하면 null. 받은 주소와 실제 단계를 함께 준다 */
export async function loadLod<T>(
  ref: LodRef,
  level: LodLevel,
  load: (url: string) => Promise<T>,
): Promise<{ src: T; url: string; level: LodLevel } | null> {
  for (const url of lodCandidates(ref, level)) {
    try {
      const src = await load(url);
      const got: LodLevel = url === ref.lod?.l2 ? 2 : url === ref.lod?.l1 ? 1 : 0;
      return { src, url, level: got };
    } catch {
      /* 다음 단계로 */
    }
  }
  return null;
}
